import { getAnalytics } from "./index";

// A parked live session waits this long for the next channel's player.
export const CHANNEL_CHANGE_WINDOW_MS = 2500;

const SLOTS = ["live", "vod"];
const LIVE_TYPES = ["LIVE", "TIMESHIFT"];

const isEmpty = (value) =>
  value === null || value === undefined || value === "";

// Runs fn and returns its result, or undefined when it throws.
const safe = (fn) => {
  try {
    return fn();
  } catch (e) {
    return undefined;
  }
};

// Maps a stream URL to the SDK streamFormat. Never throws.
export function getStreamFormat(src) {
  if (typeof src !== "string") {
    return "OTHER";
  }

  const path = src.split(/[?#]/)[0].toLowerCase();

  if (path.endsWith(".m3u8")) {
    return "HLS";
  }
  if (path.endsWith(".mpd")) {
    return "DASH";
  }
  if (path.endsWith(".mp4")) {
    return "PROGRESSIVE";
  }
  return "OTHER";
}

const getLiveMeta = (input) => {
  const { channel, cuTvProgram, isPltv } = input;

  if (!channel || isEmpty(channel.id)) {
    return null;
  }

  const channelId = String(channel.id);
  const programCode = cuTvProgram
    ? cuTvProgram.prevuecode || cuTvProgram.epgProgramId || cuTvProgram.id
    : undefined;

  if (!isEmpty(programCode)) {
    return {
      contentType: "CATCHUP",
      contentId: String(programCode),
      programId: String(programCode),
      channelId,
    };
  }

  if (isPltv) {
    return { contentType: "TIMESHIFT", channelId };
  }

  const meta = { contentType: "LIVE", channelId };
  const channelNo = isEmpty(channel.no) ? NaN : Number(channel.no);

  if (Number.isInteger(channelNo)) {
    meta.channelNo = channelNo;
  }
  return meta;
};

const getVodMeta = (input) => {
  const { vod, selectedEpisode, isRecord, fromTvGuide, isCutvVod, epgProgramId } =
    input;
  let meta;
  let contentId;

  if (isRecord) {
    contentId = (vod && (vod.prevuecode || vod.id)) || epgProgramId;
    meta = { contentType: "NPVR" };
  } else if (fromTvGuide || isCutvVod) {
    contentId = epgProgramId || (vod && (vod.prevuecode || vod.id));
    meta = { contentType: "CATCHUP" };

    if (vod && !isEmpty(vod.channelcode)) {
      meta.channelId = String(vod.channelcode);
    }
  } else {
    contentId =
      vod && vod.type === "SERIES" && selectedEpisode && selectedEpisode.id
        ? selectedEpisode.id
        : vod && vod.id;
    meta = { contentType: "VOD" };
    if (vod && (vod.type === "SERIES" || vod.type === "EPISODE")) {
      meta.contentKind = "EPISODE";
    }
  }

  if (isEmpty(contentId)) {
    return null;
  }

  meta.contentId = String(contentId);
  return meta;
};

// Builds the SDK playback metadata for the main player, or null when the
// content cannot be identified. The stream URL is never copied into it.
export function getPlaybackMeta(input) {
  try {
    if (!input || typeof input.src !== "string" || input.src === "") {
      return null;
    }

    const meta = input.isLive ? getLiveMeta(input) : getVodMeta(input);

    if (!meta) {
      return null;
    }

    meta.streamFormat = getStreamFormat(input.src);
    return meta;
  } catch (e) {
    return null;
  }
}

// Returns the <video> element under a video.js player, or null. Never throws.
export function getVideoElement(player) {
  if (!player) {
    return null;
  }

  const techEl = safe(() => player.tech({ IWillNotUseThisInPlugins: true }).el());

  if (techEl && techEl.tagName === "VIDEO") {
    return techEl;
  }

  return safe(() => player.el().querySelector("video")) || null;
}

const sameTarget = (a, b) =>
  a.contentType === b.contentType &&
  a.contentId === b.contentId &&
  a.channelId === b.channelId;

// A live HLS DVR window can have a finite duration. The route/content contract
// determines whether it is live; HTMLMediaElement.duration alone cannot do so.
export function createMediaAdapter(sdk, videoEl, meta) {
  const adapter = sdk.adapters.html5.create(videoEl);
  if (!adapter || !LIVE_TYPES.includes(meta.contentType)) return adapter;
  return {
    ...adapter,
    attach(sink) {
      adapter.attach({
        signal(signal) {
          sink.signal(signal.t === "manifest_loaded"
            ? { ...signal, isLive: true, durationMs: undefined }
            : signal);
        },
      });
    },
    snapshot() {
      return { ...adapter.snapshot(), isLive: true, durationMs: null };
    },
  };
}

// Keeps one playback session per slot ("live", "vod"). Never throws.
export function createPlaybackController({
  getSdk,
  now,
  setTimer = (fn, ms) => setTimeout(fn, ms),
}) {
  const slots = { live: null, vod: null };

  const isSlot = (slot) => SLOTS.includes(slot);

  const closeSlot = (slot, reason) => {
    const entry = slots[slot];
    slots[slot] = null;

    if (entry) {
      safe(() => entry.session.end(reason));
    }
  };

  const isParkedRecently = (entry) =>
    entry.parked && now() - entry.parkedAt <= CHANNEL_CHANGE_WINDOW_MS;

  const isChannelChange = (entry, meta) =>
    isParkedRecently(entry) &&
    LIVE_TYPES.includes(entry.meta.contentType) &&
    meta.contentType === "LIVE" &&
    entry.meta.channelId !== meta.channelId;

  // The live page remounts the player once more after a channel change (first
  // with the previous URL, then with the new one). The same target on a new
  // stream URL or <video> within the window continues the parked session.
  const isRemount = (entry, meta, videoEl, src) =>
    isParkedRecently(entry) &&
    sameTarget(entry.meta, meta) &&
    (entry.src !== src || entry.videoEl !== videoEl);

  function start(slot, meta, videoEl, { src, pageType } = {}) {
    try {
      const sdk = getSdk();

      if (!sdk || !meta || !videoEl || !isSlot(slot)) {
        return;
      }

      const current = slots[slot];
      const sameContent =
        current &&
        ((!current.parked && sameTarget(current.meta, meta)) ||
          isRemount(current, meta, videoEl, src));

      if (sameContent && current.src === src && current.videoEl === videoEl) {
        return;
      }

      const adapter = safe(() => createMediaAdapter(sdk, videoEl, meta));

      if (!adapter) {
        closeSlot(slot, "user_exit");
        return;
      }

      // Same content with a new stream URL or <video>: keep the session and
      // move it to the new adapter (the SDK detaches the old one).
      if (sameContent) {
        current.src = src;
        current.videoEl = videoEl;
        current.parked = false;
        safe(() => current.session.attachPlayer(adapter));
        return;
      }

      let session;

      if (current && isChannelChange(current, meta)) {
        session = safe(() => current.session.channelChange(meta, "guide"));
      }

      if (!session) {
        closeSlot(slot, "user_exit");
        session = safe(() =>
          sdk.createPlaybackSession(meta, {
            trigger: "user",
            source: { pageType },
          })
        );
      }

      if (!session) {
        slots[slot] = null;
        return;
      }

      slots[slot] = { session, meta, src, videoEl, parked: false };
      safe(() => session.attachPlayer(adapter));
    } catch (e) {}
  }

  function suspend(slot) {
    try {
      const entry = isSlot(slot) ? slots[slot] : null;

      if (!entry || entry.parked) {
        return;
      }

      const parkId = (entry.parkId || 0) + 1;
      entry.parked = true;
      entry.parkedAt = now();
      entry.parkId = parkId;
      safe(() => entry.session.detachPlayer());

      // Without a next channel the user has left the player. End the session
      // before the SDK closes it as PLAYER_DESTROYED (3 s without a player).
      // A timer from an earlier park of the same session does nothing.
      safe(() =>
        setTimer(() => {
          if (
            slots[slot] === entry &&
            entry.parked &&
            entry.parkId === parkId
          ) {
            closeSlot(slot, "user_exit");
          }
        }, CHANNEL_CHANGE_WINDOW_MS)
      );
    } catch (e) {}
  }

  function end(slot, reason) {
    try {
      if (isSlot(slot)) {
        closeSlot(slot, reason);
      }
    } catch (e) {}
  }

  return { start, suspend, end };
}

const controller = createPlaybackController({
  getSdk: getAnalytics,
  now: () => Date.now(),
});

export function startPlayback(slot, meta, videoEl, options) {
  controller.start(slot, meta, videoEl, options);
}

export function suspendPlayback(slot) {
  controller.suspend(slot);
}

export function endPlayback(slot, reason) {
  controller.end(slot, reason);
}

const errorHandlers = {};

// Returns the same handler per slot so player.off/on never registers twice.
export function getPlaybackErrorHandler(slot) {
  if (!errorHandlers[slot]) {
    errorHandlers[slot] = () => endPlayback(slot, "error");
  }
  return errorHandlers[slot];
}
